import { flushEarlyErrors } from '@/libs/datadog';

import bootstrapAnalytics from './bootstrapAnalytics';
import {
  ANALYTICS_BOOTSTRAP_FAILURE_MESSAGE,
  ANALYTICS_BOOTSTRAP_TIMEOUT_MESSAGE,
  ANALYTICS_BOOTSTRAP_TIMEOUT_MS
} from './constants';
import type { BootstrapAnalyticsConfig } from './types';

/**
 * Starts analytics without letting it hold up the first paint. Returns
 * immediately — never await this.
 *
 * Analytics is not a rendering dependency. The provider is third-party code
 * reaching for Amplitude, Datadog and a consent manager, and any of those legs
 * can hang; awaiting it puts the whole app behind an unbounded promise, whose
 * failure mode is a permanently blank page with a clean console. Losing
 * telemetry for a session costs far less than not rendering at all.
 *
 * Rendering ahead of initialization is safe: the provider's tracking functions
 * drop pre-init calls rather than throwing.
 *
 * Buffered early errors are replayed only once the bootstrap resolves, because
 * `flushEarlyErrors` needs a live RUM instance to replay into — flushing on
 * failure or on the watchdog would discard the buffer and uninstall the capture
 * for the rest of the session.
 *
 * Both degradation paths log at `error` level, which `initDatadogLogs`
 * (`forwardConsoleLogs`) forwards to Datadog Logs. That init runs synchronously
 * inside `bootstrapAnalytics` before the leg that can hang, so the Logs SDK is
 * up even when the provider never settles.
 */
const startAnalytics = (config: BootstrapAnalyticsConfig) => {
  const watchdog = setTimeout(() => {
    console.error(ANALYTICS_BOOTSTRAP_TIMEOUT_MESSAGE);
  }, ANALYTICS_BOOTSTRAP_TIMEOUT_MS);

  void bootstrapAnalytics(config)
    .then(flushEarlyErrors)
    .catch(error => console.error(ANALYTICS_BOOTSTRAP_FAILURE_MESSAGE, error))
    .finally(() => clearTimeout(watchdog));
};

export default startAnalytics;
